import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, TrendingUp, Shield, Clock } from 'lucide-react';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();

  const serviceDetails = {
    'viop-islemleri': {
      title: 'VİOP İŞLEMLERİ',
      description: 'Vadeli işlem ve opsiyon sözleşmeleri',
      image: 'https://customer-assets.emergentagent.com/job_colendi-replica/artifacts/qih3ju09_viop.png',
      content: 'VİOP piyasasında futures ve opsiyon işlemlerinizi gerçekleştirin.',
      features: [
        'Kaldıraçlı işlem imkanı',
        'Riskten korunma stratejileri',
        'Geniş ürün yelpazesi',
        'Uzman destek'
      ]
    },
    'pay-alim-satim': {
      title: 'PAY ALIM-SATIM',
      description: 'Hisse senedi alım satım işlemleriniz için güvenli platform',
      image: 'https://customer-assets.emergentagent.com/job_colendi-replica/artifacts/941o2s4c_payalim.png',
      content: 'Borsa İstanbul\'da işlem gören tüm hisse senetlerini alıp satabilirsiniz.',
      features: [
        'Anlık piyasa verileri',
        'Düşük komisyon oranları',
        'Hızlı emir iletimi',
        'Gelişmiş analiz araçları'
      ]
    },
    'yatirim-fonlari': {
      title: 'YATIRIM FONLARI',
      description: 'Profesyonel yönetilen yatırım fonları',
      image: 'https://customer-assets.emergentagent.com/job_colendi-replica/artifacts/f4p0nkgh_ytf.png',
      content: 'Farklı risk profillerine uygun yatırım fonları ile portföyünüzü çeşitlendirin.',
      features: [
        'Çeşitli fon seçenekleri',
        'Profesyonel portföy yönetimi',
        'Düşük başlangıç tutarı',
        'Anlık fon performans takibi'
      ]
    },
    'yatirim-danismanligi': {
      title: 'YATIRIM DANIŞMANLIĞI',
      description: 'Uzman yatırım danışmanlığı hizmeti',
      image: 'https://customer-assets.emergentagent.com/job_colendi-replica/artifacts/gokfabac_ytd.png',
      content: 'Uzman yatırım danışmanlarımız ile bilinçli yatırım kararları alın.',
      features: [
        'Kişiye özel stratejiler',
        'Piyasa analizleri',
        'Portföy optimizasyonu',
        'Düzenli rapor ve takip'
      ]
    }
  };

  const service = serviceDetails[serviceId] || serviceDetails['viop-islemleri'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white py-16">
        <div className="container mx-auto px-4">
          <Link to="/hizmetler" className="text-teal-100 hover:text-white mb-4 inline-block">
            ← Tüm Hizmetler
          </Link>
          <h1 className="text-4xl font-bold mb-4">{service.title}</h1>
          <p className="text-xl text-teal-50">{service.description}</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <img 
                src={service.image} 
                alt={service.title}
                className="w-48 h-48 mx-auto object-contain rounded-lg"
              />
            </div>
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Hizmet Hakkında</h2>
                <p className="text-gray-600 text-lg leading-relaxed">{service.content}</p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Öne Çıkan Özellikler</h3>
                <div className="space-y-3">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <a
                href="https://my.scuzdan.com/register"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors"
              >
                Hemen Başlayın
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Neden CLND Menkul?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="text-teal-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Rekabetçi Oranlar</h3>
              <p className="text-gray-600">Sektörün en uygun komisyon ve faiz oranları</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-teal-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Güvenli Platform</h3>
              <p className="text-gray-600">SPK lisanslı, tam güvenli yatırım ortamı</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="text-teal-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">7/24 Destek</h3>
              <p className="text-gray-600">Her zaman ulaşabileceğiniz uzman destek ekibi</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
